import {Exclude, Expose, instanceToPlain, plainToInstance, Transform, Type} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../web/validation";
import {getDatabaseCodeTransformer} from "./transfomers/transformers";
import {toTransportCategory, TransportCategory} from "./transport-category";
import {TransportMethod, toTransportMethod} from "./transport-method";
import {CompanyType, toCompanyType} from "./company"
import {TicketCodeArrayValidator, TicketCodeEntity, TicketCodeValidator} from "./ticket-code";
import {CostedEntity} from "./costed-entity";
import {ObjectCostRateModelEntity} from "./object-cost-rate-model";

export const TransportLineItemValidator = t.intersection([
  t.type({
    id: t.string,
    invoiceId: t.string,
    resourceId: t.string,
    seqNum: t.number,
  }),
  t.partial({
    sortField1: t.string,
    sortField2: t.string,
    pickupId: t.string,
    deliveryId: t.string,
    ticketNumber: t.string,
    workOrderNumber: t.string,
    quantity: t.number,
    quantityUnitOfMeasure: t.number,
    regionId: t.string,
    personId: t.string,
    personName: t.string,
    waterTypeId: t.string,
    waterTypeName: t.string,
    pickupCreateDttm: t.number,
    pickupVehicleId: t.string,
    pickupVehicleName: t.string,
    pickupTrailerId: t.string,
    pickupTrailerName: t.string,
    pickupContainerId: t.string,
    pickupContainerName: t.string,
    pickupSiteId: t.string,
    pickupSiteName: t.string,
    pickupSiteCompanyId: t.string,
    pickupSiteCompanyName: t.string,
    pickupSiteCompanyType: t.string,
    pickupServiceTypeId: t.string,
    pickupServiceTypeName: t.string,
    pickupWellId: t.string,
    pickupWellName: t.string,
    deliveryCreateDttm: t.number,
    deliveryVehicleId: t.string,
    deliveryVehicleName: t.string,
    deliveryTrailerId: t.string,
    deliveryTrailerName: t.string,
    deliveryContainerId: t.string,
    deliveryContainerName: t.string,
    deliverySiteId: t.string,
    deliverySiteName: t.string,
    deliverySiteCompanyId: t.string,
    deliverySiteCompanyName: t.string,
    deliverySiteCompanyType: t.string,
    deliveryWellId: t.string,
    deliveryWellName: t.string,
    deliveryServiceTypeId: t.string,
    deliveryServiceTypeName: t.string,
    wellId: t.string,
    wellName: t.string,
    wellAllocation: t.number,
    category: t.string,
    method: t.string,
    specialCostType: t.string,
    rateId: t.string,
    rateName: t.string,
    rateModelId: t.string,
    rateModelName: t.string,
    rateModelType: t.string,
    coding: t.string,
    disputed: t.boolean,
    timeQty: t.number,
    freightQty: t.number,
    loadQty: t.number,
    timeRate: t.number,
    freightRate: t.number,
    loadRate: t.number,
    loadCost: t.number,
    timeCost: t.number,
    freightCost: t.number,
    cost: t.number,
    createDttm: t.number,
    lastModifiedDttm: t.number,
    //
    ticketCodeList: TicketCodeArrayValidator,
    ticketCode: TicketCodeValidator,
    databaseName: t.string,
  }),
]);

export const TransportLineItemArrayValidator = t.array(
  TransportLineItemValidator
);

export type TransportLineItem = t.TypeOf<
  typeof TransportLineItemValidator
>;
export type TransportLineItemArray = t.TypeOf<
  typeof TransportLineItemArrayValidator
>;

@Exclude()
export class TransportLineItemEntity extends CostedEntity {
  @Expose()
  id!: string;

  @Expose()
  invoiceId!: string

  @Expose()
  seqNum!: number

  @Expose()
  resourceId!: string

  @Expose()
  sortField1?: string

  @Expose()
  sortField2?: string

  @Expose()
  pickupId?: string

  @Expose()
  deliveryId?: string

  @Expose()
  ticketNumber?: string

  @Expose()
  workOrderNumber?: string

  @Expose()
  personId?: string

  @Expose()
  personName?: string

  @Expose({name: "waterTypeId"})
  freightTypeId?: string

  @Expose({name: "waterTypeName"})
  freightTypeName?: string

  @Expose()
  pickupCreateDttm?: number

  @Expose()
  pickupVehicleId?: string

  @Expose()
  pickupVehicleName?: string

  @Expose()
  pickupTrailerId?: string

  @Expose()
  pickupTrailerName?: string

  @Expose()
  pickupContainerId?: string;

  @Expose()
  pickupContainerName?: string;

  @Expose()
  pickupSiteId?: string;

  @Expose()
  pickupSiteName?: string;

  @Expose()
  pickupSiteCompanyId?: string;

  @Expose()
  pickupSiteCompanyName?: string;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toCompanyType))
  pickupSiteCompanyType?: CompanyType;

  @Expose()
  pickupWellId?: string;

  @Expose()
  pickupWellName?: string;

  @Expose()
  pickupServiceTypeId?: string;

  @Expose()
  pickupServiceTypeName?: string;

  @Expose()
  deliveryCreateDttm?: number

  @Expose()
  deliveryVehicleId?: string

  @Expose()
  deliveryVehicleName?: string

  @Expose()
  deliveryTrailerId?: string

  @Expose()
  deliveryTrailerName?: string

  @Expose()
  deliveryContainerId?: string;

  @Expose()
  deliveryContainerName?: string;

  @Expose()
  deliverySiteId?: string;

  @Expose()
  deliverySiteName?: string;

  @Expose()
  deliverySiteCompanyId?: string;

  @Expose()
  deliverySiteCompanyName?: string;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toCompanyType))
  deliverySiteCompanyType?: CompanyType;

  @Expose()
  deliveryWellId?: string;

  @Expose()
  deliveryWellName?: string;

  @Expose()
  deliveryServiceTypeId?: string;

  @Expose()
  deliveryServiceTypeName?: string;

  @Expose()
  quantity?: number;

  @Expose()
  quantityUnitOfMeasure?: string;

  @Expose()
  wellId?: string

  @Expose()
  wellName?: string

  @Expose()
  wellAllocation?: number

  @Expose()
  @Transform(getDatabaseCodeTransformer(toTransportCategory))
  category?: TransportCategory;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toTransportMethod))
  method?: TransportMethod;

  @Expose()
  specialCostType?: string

  @Expose()
  rateId?: string

  @Expose()
  rateName?: string

  @Expose()
  rateModelId?: string

  @Expose()
  rateModelName?: string

  @Expose()
  rateModelType?: string

  @Expose()
  coding?: string

  @Expose()
  disputed?: boolean

  @Expose()
  timeQty?: number

  @Expose()
  freightQty?: number

  @Expose()
  loadQty?: number

  @Expose()
  timeRate?: number

  @Expose()
  freightRate?: number

  @Expose()
  loadRate?: number

  @Expose({name: "loadCost"})
  private _loadCost?: number

  @Expose({name: "timeCost"})
  private _timeCost?: number

  @Expose({name: "freightCost"})
  private _freightCost?: number

  @Expose({name: "cost"})
  private _cost?: number

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  @Expose({name: "ticketCode"})
  @Type(() => TicketCodeEntity)
  code?: TicketCodeEntity

  /**
   * @deprecated
   */
  @Expose({name: "ticketCodeList"})
  @Type(() => TicketCodeEntity)
  codes?: TicketCodeEntity[]

  readonly entityType = 'TLI';

  get displayName(): Readonly<string> {
    return this.id;
  }

  /**
   * Constructor for TransportLineItemEntity.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
    super()
  }

  static from(
    tli: TransportLineItem
  ): TransportLineItemEntity {
    return plainToInstance(TransportLineItemEntity, tli, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<TransportLineItem> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(TransportLineItemValidator, object);
  }

  asTypeValue(): TransportLineItem {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(TransportLineItemValidator, object);
  }

  get objectCostRateModels() : ObjectCostRateModelEntity[] | undefined {
    const ocrms : ObjectCostRateModelEntity[] = []

    if (this._freightCost) {
      const ocrm = new ObjectCostRateModelEntity();
      ocrm.id = this.id
      ocrm.objectId = this.id
      ocrm.objectType = this.entityType
      ocrm.rateModelId = this.rateModelId ?? this.id
      ocrm.rateModelName = this.rateModelName ?? "Default"
      ocrm.billableQty = this.freightQty
      ocrm.totalCost = this._freightCost
      ocrm.freightCost = this._freightCost
      ocrm.freightRate = this.freightRate
      ocrm.wellId = this.wellId
      ocrm.wellName = this.wellName
      ocrm.wellAllocation = this.wellAllocation
      ocrms.push(ocrm)
    }

    if (this._timeCost) {
      const ocrm = new ObjectCostRateModelEntity();
      ocrm.id = this.id
      ocrm.objectId = this.id
      ocrm.objectType = this.entityType
      ocrm.rateModelId = this.rateModelId ?? this.id
      ocrm.rateModelName = this.rateModelName ?? "Default"
      ocrm.billableHours = this.timeQty
      ocrm.totalCost = this._timeCost
      ocrm.timeCost = this._timeCost
      ocrm.timeRate = this.timeRate
      ocrm.wellId = this.wellId
      ocrm.wellName = this.wellName
      ocrm.wellAllocation = this.wellAllocation
      ocrms.push(ocrm)
    }

    if (this._loadCost) {
      const ocrm = new ObjectCostRateModelEntity();
      ocrm.id = this.id
      ocrm.objectId = this.id
      ocrm.objectType = this.entityType
      ocrm.rateModelId = this.rateModelId ?? this.id
      ocrm.rateModelName = this.rateModelName ?? "Default"
      ocrm.billableQty = this.loadQty
      ocrm.totalCost = this._loadCost
      ocrm.loadCost =this._loadCost
      ocrm.loadRate = this.loadRate
      ocrm.wellId = this.wellId
      ocrm.wellName = this.wellName
      ocrm.wellAllocation = this.wellAllocation
      ocrms.push(ocrm)
    }

    return ocrms
  }

  get freightCost(): number {
    return this._freightCost ?? 0;
  }

  set freightCost(value: number) {
    this._freightCost = value;
  }

  get timeCost(): number {
    return this._timeCost ?? 0;
  }

  set timeCost(value: number) {
    this._timeCost = value;
  }

  get loadCost(): number {
    return this._loadCost ?? 0;
  }

  set loadCost(value: number) {
    this._loadCost = value;
  }

  get totalCost(): number {
    return this._cost ?? 0
  }

  set totalCost(value: number) {
    this._cost = value;
  }
}
